import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LIBRARY_IMAGE } from "@/lib/content";
import SEO from "@/components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("Reset link is missing its token.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/password-reset/confirm`, {
        token,
        new_password: password,
      });
      toast.success("Password updated. Please sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) || err.message
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen pt-32 pb-20 bg-ivory grid grid-cols-1 lg:grid-cols-12"
      data-testid="reset-password-page"
    >
      <SEO
        title="Choose a New Password"
        description="Choose a new password for your Lone Star Counsel client-portal account."
        path="/reset-password"
        noindex
      />
      <aside className="hidden lg:block lg:col-span-5 panel-navy relative overflow-hidden">
        <img
          src={LIBRARY_IMAGE}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-navy/70"></div>
        <div className="relative h-full flex flex-col justify-end p-16 text-ivory">
          <div className="kicker kicker-ivory">Portal Access</div>
          <h2 className="mt-6 font-serif text-5xl leading-[1.0]">
            Choose a <span className="italic text-brass">new key.</span>
          </h2>
          <p className="mt-6 text-ivory/70 font-light leading-relaxed max-w-sm">
            Twelve characters or more, with at least one letter and one digit
            or symbol. Keep it somewhere quiet.
          </p>
        </div>
      </aside>

      <div className="lg:col-span-6 lg:col-start-7 flex items-center px-6 md:px-12 py-20">
        <form
          onSubmit={submit}
          className="w-full max-w-md mx-auto"
          data-testid="reset-password-form"
        >
          <div className="kicker">New Password</div>
          <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.98]">
            Choose a<br />
            <span className="italic text-oxblood">new password.</span>
          </h1>

          <div className="mt-12 space-y-8">
            <label className="block">
              <div className="field-label mb-2">New Password</div>
              <input
                className="field"
                type="password"
                required
                minLength={12}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="reset-password-new"
                autoComplete="new-password"
              />
              <div className="mt-2 text-xs text-slate2 font-cormorant italic">
                Minimum 12 characters · At least one letter and one digit or symbol
              </div>
            </label>
            <label className="block">
              <div className="field-label mb-2">Confirm Password</div>
              <input
                className="field"
                type="password"
                required
                minLength={12}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-password-confirm"
                autoComplete="new-password"
              />
            </label>
          </div>

          {error && (
            <div
              className="mt-6 text-oxblood font-serif italic text-sm"
              data-testid="reset-password-error"
            >
              — {error}
            </div>
          )}

          <div className="mt-10 flex items-center justify-between">
            <button
              type="submit"
              disabled={loading || !token}
              className="btn-primary disabled:opacity-40"
              data-testid="reset-password-submit"
            >
              {loading ? "Updating…" : "Update Password"}
            </button>
            <Link
              to="/login"
              className="link-underline font-serif italic"
              data-testid="reset-password-back-link"
            >
              Back to sign in
            </Link>
          </div>

          <div className="mt-12 hairline pt-6 text-xs uppercase tracking-[0.2em] text-slate2/70">
            Reset links are valid for one hour · Single use only
          </div>
        </form>
      </div>
    </div>
  );
}
